const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

// Sources dont on n'envoie AUCUN événement Meta (ex. trafic TikTok : géré par AppsFlyer, pas Facebook).
// Une fois détecté, on mémorise pour toute la session (les navigations SPA perdent le paramètre d'URL).
const SUPPRESS_SOURCES = ['tiktok'];
const META_SUPPRESS_KEY = 'ogg_meta_suppress';

export const metaSuppressed = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (sessionStorage.getItem(META_SUPPRESS_KEY) === '1') return true;
  } catch (_) {}
  let src = '';
  try {
    src = (new URLSearchParams(window.location.search).get('utm_source') || '').toLowerCase();
  } catch (_) {}
  if (src && SUPPRESS_SOURCES.includes(src)) {
    try { sessionStorage.setItem(META_SUPPRESS_KEY, '1'); } catch (_) {}
    return true;
  }
  return false;
};

// fbq supporte plusieurs pixels en parallèle (un fbq('init', id) par pixel) : tout
// fbq('track', ...) diffuse ensuite l'événement à TOUS les pixels inités. On peut donc
// avoir le pixel OuiGoo + le pixel d'un marchand actifs en même temps sur la page catalogue.
//
// On suit ici les IDs déjà inités pour que ce soit idempotent et surtout indépendant de
// l'ordre d'appel : le pixel marchand (déclenché depuis CataloguePage) peut charger le
// script fbevents.js avant le pixel OuiGoo (déclenché depuis App.js) selon l'ordre des
// effets React — chaque init doit rester possible quel que soit qui arrive en premier.
const initedPixelIds = new Set();

const ensureFbqScript = () => {
  if (window.fbq) return;

  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      if (n.callMethod) {
        n.callMethod.apply(n, arguments);
      } else {
        n.queue.push(arguments);
      }
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
};

const initPixelId = (id) => {
  if (!id || initedPixelIds.has(id)) return;
  // Trafic d'une source exclue (TikTok) : on ne charge aucun pixel, ni OuiGoo ni marchand.
  if (metaSuppressed()) return;
  ensureFbqScript();
  window.fbq('init', id);
  window.fbq('trackSingle', id, 'PageView');
  initedPixelIds.add(id);
};

export const initMetaPixel = () => {
  if (!META_PIXEL_ID) {
    console.warn('Meta Pixel ID missing');
    return;
  }
  initPixelId(META_PIXEL_ID);
};

// Pixel Meta d'un marchand (catalogue), configuré depuis son app marchand.
// Coexiste avec le pixel OuiGoo — les deux reçoivent les mêmes événements de la page.
export const initMerchantPixel = (pixelId) => {
  if (!pixelId) return;
  const clean = String(pixelId).trim();
  // Un pixel Meta valide est purement numérique (même validation que le backend).
  if (!/^[0-9]{5,20}$/.test(clean)) {
    console.warn('Merchant pixel id invalide, ignoré:', clean);
    return;
  }
  initPixelId(clean);
};

export const trackPageView = () => {
  if (window.fbq && !metaSuppressed()) {
    window.fbq('track', 'PageView');
  }
};

export const trackLead = (params = {}) => {
  if (window.fbq && !metaSuppressed()) {
    window.fbq('track', 'Lead', {
      content_name: 'ouigoo_demo_request',
      page: 'ouigoo_app',
      ...params,
    });
  }
};

export const trackDemoClick = () => {
  if (window.fbq && !metaSuppressed()) {
    window.fbq('trackCustom', 'DemoClick', {
      page: 'ouigoo_app',
    });
  }
};

// Conversion : commande de livraison passée sur la vitrine.
// currency = devise réelle de la transaction (XOF). Le Business Manager (EUR)
// convertit automatiquement via la parité fixe FCFA/euro (1 EUR = 655,957 XOF).
export const trackPurchase = ({ value, currency = 'XOF', orderId, params = {} } = {}) => {
  if (window.fbq && !metaSuppressed()) {
    window.fbq('track', 'Purchase', {
      value: Number(value) || 0,
      currency,
      content_name: 'delivery_order',
      ...(orderId ? { order_id: orderId } : {}),
      ...params,
    });
  }
};

// Étape intermédiaire du tunnel : l'utilisateur a obtenu un devis (tarif simulé).
export const trackInitiateCheckout = ({ value, currency = 'XOF', params = {} } = {}) => {
  if (window.fbq && !metaSuppressed()) {
    window.fbq('track', 'InitiateCheckout', {
      value: Number(value) || 0,
      currency,
      content_name: 'delivery_quote',
      ...params,
    });
  }
};

// Ajoute la source (UTM) aux événements pour filtrer par canal dans Events Manager.
const utmFields = (utm = {}) => ({
  source: utm.utm_source || '',
  medium: utm.utm_medium || '',
  campaign: utm.utm_campaign || '',
  content: utm.utm_content || '',
});

export const trackStoreClick = (store, utm = {}) => {
  if (window.fbq && !metaSuppressed()) {
    window.fbq('trackCustom', 'StoreClick', {
      store,
      page: 'ouigoo_app',
      ...utmFields(utm),
    });
  }
};

export const trackPlayStoreClick = (utm = {}) => {
  if (window.fbq && !metaSuppressed()) {
    window.fbq('trackCustom', 'PlayStoreClick', {
      page: 'ouigoo_app',
      store: 'play-store',
      ...utmFields(utm),
    });
  }
};

export const trackAppStoreClick = (utm = {}) => {
  if (window.fbq && !metaSuppressed()) {
    window.fbq('trackCustom', 'AppStoreClick', {
      page: 'ouigoo_app',
      store: 'app-store',
      ...utmFields(utm),
    });
  }
};
